'use client'

import type { ChangeEvent } from 'react'
import type {
	CheckboxProps,
	SRT_Row,
	SRT_RowData,
	SRT_TableInstance,
} from '../../types'

import { Checkbox } from '@/shared/components/ui/checkbox-native'
import { Radio } from '@/shared/components/ui/radio'

import {
	getIsRowSelected,
	getSRT_RowSelectionHandler,
	getSRT_SelectAllHandler,
} from '../../utils/row.utils'

export type SRT_SelectCheckboxProps<TData extends SRT_RowData> = {
	row?: SRT_Row<TData>
	staticRowIndex?: number
	table: SRT_TableInstance<TData>
	className?: string
} & CheckboxProps

export const SRT_SelectCheckbox = <TData extends SRT_RowData>({
	row,
	table,
}: SRT_SelectCheckboxProps<TData>) => {
	const {
		options: { enableMultiRowSelection, selectAllMode },
		getState,
	} = table
	const { isLoading } = getState()

	const selectAll = !row

	const allRowsSelected = selectAll
		? selectAllMode === 'page'
			? table.getIsAllPageRowsSelected()
			: selectAllMode === 'tree'
				? table.getIsAllTreeRowsSelected()
				: table.getIsAllRowsSelected()
		: undefined

	const isChecked = selectAll
		? allRowsSelected
		: getIsRowSelected({ row, table })

	const onSelectionChange = row
		? getSRT_RowSelectionHandler({ row, table })
		: undefined
	const onSelectAllChange = getSRT_SelectAllHandler({ table })

	const commonProps = {
		checked: isChecked,
		disabled:
			isLoading || (row && !row.getCanSelect()) || row?.id === 'srt-row-create',
		onChange: (event: ChangeEvent<HTMLInputElement>) => {
			selectAll ? onSelectAllChange(event) : onSelectionChange?.(event)
		},
	}
	if (enableMultiRowSelection === false) {
		return <Radio {...commonProps} value={row?.id ?? 'select-all'} />
	} else {
		return (
			<Checkbox
				indeterminate={
					!isChecked && selectAll
						? table.getIsSomeRowsSelected()
						: row?.getIsSomeSelected() && row.getCanSelectSubRows()
				}
				{...commonProps}
			/>
		)
	}
}
